import { FC } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Outlet, RouterProvider } from 'react-router-dom';
import { ThemeProvider } from '~/hooks/useTheme';
import {
  createBrowserRouter,
  NotFoundPlaceholder,
  OrganizationRouteObject,
  RouteObject,
  UserRouteObject,
  WorkspaceRouteObject,
} from '~/routes/app';
import { UserRoot } from '~/routes/user/root/views';
import { OrganizationRoot } from '~/routes/organization/root/views';
import { WorkspaceRoot } from '~/routes/workspace/root/views';
import { DataExplorerWorkItemDetails } from '~/components/dataExplorer/workItem/WorkItemDetails';
import { DataExplorer } from '~/components/dataExplorer';
import ViewLoader from '~/components/ViewLoader';
import SigninCallback from '~/auth/SigninCallback';
import SignoutCallback from '~/auth/SignoutCallback';
import EmailNotVerified from '~/auth/EmailNotVerified';
import AccountNotActive from '~/auth/AccountNotActive';
import SigninPage from '~/auth/SigninPage';
import queryClient from './queryClient';
import {
  HomeLayer,
  OrganizationLayer,
  ProtectedLayer,
  RootLayer,
  UserLayer,
  WorkspaceLayer,
} from './Layer';

import './i18n/i18n';
import type { ParseRoutePaths, ParseTabRoutePaths } from './routes/app/types';

const partialExplorerRoute = {
  Component: DataExplorer,
  children: [{ path: 'workItem/:workItemId', Component: DataExplorerWorkItemDetails }],
} as const satisfies RouteObject;

const workspaceRoutes = [
  { index: true, Component: WorkspaceRoot },
  {
    title: 'Configuration',
    category: 'workspace_settings',
    path: 'settings',
    lazy: () => import('~/routes/workspace/settings/views/Settings'),
    children: [
      { index: true, Component: Outlet, tab: true },
      {
        path: 'members',
        Component: Outlet,
        restrictions: { workspace: ['read_users'] },
        tab: true,
      },
      {
        path: 'log',
        Component: Outlet,
        restrictions: { workspace: ['view_audit_log'] },
        tab: true,
      },
      {
        path: 'assistant-user-import',
        Component: Outlet,
        restrictions: { workspace: ['import_users'] },
        tab: true,
      },
    ],
  },
  {
    title: 'Activity',
    category: 'activities',
    path: 'activity',
    lazy: () => import('~/routes/workspace/activity/views/Activity'),
    children: [
      {
        Component: Outlet,
        restrictions: { workspace: ['read_processes'] },
        tab: true,
        children: [{ ...partialExplorerRoute, path: 'explorer/process/:processId/runs/:pRunId' }],
      },
      {
        path: 'scheduledRuns',
        Component: Outlet,
        restrictions: { workspace: ['read_processes'] },
        tab: true,
      },
      {
        path: 'stepRuns',
        Component: Outlet,
        restrictions: { workspace: ['read_processes'] },
        tab: true,
        children: [
          {
            ...partialExplorerRoute,
            path: 'explorer/process/:processId/runs/:pRunId/stepRuns/:stepRunId',
          },
        ],
      },
      {
        path: 'assistantRuns',
        Component: Outlet,
        restrictions: { workspace: ['read_assistants'] },
        tab: true,
      },
    ],
  },
  {
    title: 'Processes',
    category: 'processes',
    restrictions: { workspace: ['read_processes'] },
    path: 'processes',
    lazy: () => import('~/routes/workspace/processes/views/Processes'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'performance', Component: Outlet, tab: true },
      { ...partialExplorerRoute, path: 'explorer/process/:processId' },
    ],
  },
  {
    title: 'Process',
    category: 'processes',
    restrictions: { workspace: ['read_processes'] },
    path: 'processes/:processId',
    lazy: () => import('~/routes/workspace/processes/views/Process'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'performance', Component: Outlet, tab: true },
      { path: 'documentation', Component: Outlet, tab: true },
      { path: 'onboarding', Component: Outlet, tab: true },
      { ...partialExplorerRoute, path: 'explorer' },
      { ...partialExplorerRoute, path: 'explorer/runs/:pRunId' },
    ],
  },
  {
    title: 'Process Run',
    category: 'processes',
    restrictions: { workspace: ['read_processes'] },
    path: 'processes/:processId/runs/:pRunId',
    lazy: () => import('~/routes/workspace/processes/views/ProcessRun'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'timeline', Component: Outlet, tab: true },
      { ...partialExplorerRoute, path: 'explorer' },
      { ...partialExplorerRoute, path: 'explorer/stepRuns/:stepRunId' },
    ],
  },
  {
    title: 'Step Run',
    category: 'processes',
    restrictions: { workspace: ['read_processes'] },
    path: 'processes/:processId/runs/:pRunId/stepRuns/:stepRunId',
    lazy: () => import('~/routes/workspace/processes/views/StepRun'),
    children: [
      {
        path: 'console',
        Component: Outlet,
        tab: true,
        children: [{ ...partialExplorerRoute, path: 'explorer' }],
      },
      {
        path: 'artifacts',
        Component: Outlet,
        tab: true,
        children: [{ ...partialExplorerRoute, path: 'explorer' }],
      },
      {
        path: 'timeline',
        Component: Outlet,
        tab: true,
        children: [{ ...partialExplorerRoute, path: 'explorer' }],
      },
      { ...partialExplorerRoute, path: 'explorer' },
    ],
  },
  {
    title: 'Assistants',
    category: 'assistants',
    restrictions: { workspace: ['read_assistants'] },
    path: 'assistants',
    lazy: () => import('~/routes/workspace/assistants/views/Assistants'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'runs', Component: Outlet, tab: true },
    ],
  },
  {
    title: 'Assistant',
    category: 'assistants',
    restrictions: { workspace: ['read_assistants'] },
    path: 'assistants/:assistantId',
    lazy: () => import('~/routes/workspace/assistants/views/Assistant'),
  },
  {
    title: 'Assistant Run',
    category: 'assistants',
    restrictions: { workspace: ['read_assistants'] },
    path: 'assistants/:assistantId/runs/:assistantRunId',
    lazy: () => import('~/routes/workspace/assistants/views/AssistantRun'),
  },
  {
    title: 'Tasks',
    category: 'dead_end',
    restrictions: { workspace: ['read_robots'] },
    path: 'robots',
    lazy: () => import('~/routes/workspace/robots/views/Robots'),
  },
  {
    title: 'Tasks',
    category: 'tasks',
    restrictions: { workspace: ['read_robots'] },
    path: 'tasks',
    lazy: () => import('~/routes/workspace/robots/views/Robots'),
  },
  {
    title: 'Tasks',
    category: 'dead_end',
    restrictions: { workspace: ['read_robots'] },
    path: 'robots/:robotId', // old route that is kept for now
    lazy: () => import('~/routes/workspace/robots/views/Robot'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'processes', Component: Outlet, tab: true },
      { path: 'assistants', Component: Outlet, tab: true },
      { path: 'tasks', Component: Outlet, tab: true },
    ],
  },
  {
    title: 'Tasks',
    category: 'tasks',
    restrictions: { workspace: ['read_robots'] },
    path: 'tasks/:robotId',
    lazy: () => import('./routes/workspace/robots/views/Robot'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'processes', Component: Outlet, tab: true },
      { path: 'assistants', Component: Outlet, tab: true },
      { path: 'tasks', Component: Outlet, tab: true },
      {
        path: 'edit',
        title: 'Task Editor',
        restrictions: { workspace: ['write_robots'] },
        lazy: () => import('~/routes/workspace/robots/views/RobotCodeEditor'),
      },
    ],
  },
  {
    title: 'Workers',
    category: 'workers',
    restrictions: { workspace: ['read_workers'] },
    path: 'workers',
    lazy: () => import('~/routes/workspace/workers/views/Workers'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'groups', Component: Outlet, tab: true },
      { path: 'on-demand', Component: Outlet, tab: true },
    ],
  },
  {
    title: 'Worker Group',
    category: 'workers',
    restrictions: { workspace: ['read_workers'] },
    path: 'workers/groups/:workerGroupId',
    lazy: () => import('~/routes/workspace/workers/views/WorkerGroup'),
    children: [
      { index: true, Component: Outlet, tab: true },
      {
        path: 'step-runs',
        Component: Outlet,
        restrictions: { workspace: ['read_runs'] },
        tab: true,
      },
      {
        path: 'processes',
        Component: Outlet,
        restrictions: { workspace: ['read_processes'] },
        tab: true,
      },
    ],
  },
  {
    title: 'On-demand Worker',
    category: 'workers',
    restrictions: { workspace: ['read_workers'] },
    path: 'workers/on-demand/:workerId',
    lazy: () => import('~/routes/workspace/workers/views/OnDemandWorker'),
    children: [
      {
        index: true,
        Component: Outlet,
        restrictions: { workspace: ['read_processes'] },
        tab: true,
      },
    ],
  },
  {
    title: 'Self-hosted Worker',
    category: 'workers',
    restrictions: { workspace: ['read_workers'] },
    path: 'workers/:workerId',
    lazy: () => import('~/routes/workspace/workers/views/Worker'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'groups', Component: Outlet, tab: true },
      {
        path: 'processes',
        Component: Outlet,
        restrictions: { workspace: ['read_processes'] },
        tab: true,
      },
      { path: 'event-log', Component: Outlet, tab: true },
      { path: 'details', Component: Outlet, tab: true },
    ],
  },
  {
    title: 'Vault',
    category: 'vault',
    restrictions: { workspace: ['read_vault'] },
    path: 'vault',
    lazy: () => import('~/routes/workspace/vault/views/Vault'),
  },
  {
    title: 'Integrations',
    category: 'integrations',
    restrictions: { workspace: ['read_workspace_keys'] },
    path: 'api',
    lazy: () => import('~/routes/workspace/api/views/WorkspaceKeys'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'webhooks', Component: Outlet, tab: true },
    ],
  },
  {
    title: 'Webhooks',
    category: 'integrations',
    restrictions: { workspace: ['read_workspace_keys'] },
    path: 'api/webhooks/:webhookId',
    lazy: () => import('~/routes/workspace/api/views/Webhook'),
  },
  {
    title: 'Asset Storage',
    category: 'assets',
    restrictions: { workspace: ['read_asset_storage_assets'] },
    path: 'asset-storage',
    lazy: () => import('~/routes/workspace/asset-storage/views/AssetStorage'),
  },
  {
    title: 'Assets',
    category: 'assets',
    restrictions: { workspace: ['read_asset_storage_assets'] },
    path: 'asset-storage/:assetId',
    lazy: () => import('~/routes/workspace/asset-storage/views/AssetItem'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'log', Component: Outlet, tab: true },
    ],
  },
  { category: 'dead_end', index: true, path: '*', Component: NotFoundPlaceholder },
] as const satisfies readonly WorkspaceRouteObject[];

const organizationRoutes = [
  { index: true, Component: OrganizationRoot },
  {
    title: 'Create Workspace',
    category: 'onboarding',
    restrictions: { userRole: 'base', organization: ['manage_workspaces'] },
    path: 'create-workspace',
    lazy: () => import('~/routes/user/onboarding/views/CreateWorkspace'),
  },
  {
    title: 'Configuration',
    category: 'organization_settings',
    path: 'settings',
    lazy: () => import('~/routes/organization/settings/views/Settings'),
    children: [
      { index: true, Component: Outlet, tab: true },
      {
        path: 'members',
        Component: Outlet,
        restrictions: { organization: ['view_org_users'] },
        tab: true,
      },
      { path: 'integrations', Component: Outlet, tab: true },
      {
        path: 'log',
        Component: Outlet,
        restrictions: { organization: ['view_audit_log'] },
        tab: true,
      },
      {
        path: 'privacy-compliance',
        Component: Outlet,
        restrictions: { organizationRole: 'owner' },
        tab: true,
      },
    ],
  },
  {
    title: 'Subscription',
    category: 'subscription',
    restrictions: { organization: ['manage_billing'] },
    path: 'settings/billing',
    lazy: () => import('~/routes/organization/settings/views/BillingContent'),
    children: [
      { index: true, Component: Outlet, tab: true },
      {
        path: 'billing-info',
        Component: Outlet,
        restrictions: { blacklistSubscription: ['enterprise30'], action: 'notFound' },
        tab: true,
      },
      { path: 'invoices', Component: Outlet, tab: true },
      {
        path: 'cost-management',
        Component: Outlet,
        restrictions: { blacklistSubscription: ['enterprise30'], action: 'notFound' },
        tab: true,
      },
    ],
  },
  {
    title: 'Subscribe',
    category: 'subscription',
    restrictions: {
      organization: ['manage_billing'],
      blacklistSubscription: ['enterprise30'],
      action: 'notFound',
    },
    path: 'subscribe',
    lazy: () => import('~/routes/organization/settings/views/Subscribe'),
  },
  {
    title: 'Dashboard',
    category: 'analytics',
    restrictions: { organization: ['view_exec_dashboards'] },
    path: 'dashboard',
    lazy: () => import('~/routes/organization/dashboard/views/OrganizationDashboard'),
  },
  {
    title: 'Dashboard',
    category: 'analytics',
    restrictions: { organization: ['view_exec_dashboards'] },
    path: 'dashboard/value-generated',
    lazy: () => import('~/routes/organization/dashboard/views/DrillDownTotalValue'),
  },
  {
    title: 'Dashboard',
    category: 'analytics',
    restrictions: { organization: ['view_exec_dashboards'] },
    path: 'dashboard/run-minutes',
    lazy: () => import('~/routes/organization/dashboard/views/DrillDownRunMinutes'),
  },
  {
    title: 'Dashboard',
    category: 'analytics',
    restrictions: { organization: ['view_exec_dashboards'] },
    path: 'dashboard/processes',
    lazy: () => import('~/routes/organization/dashboard/views/DrillDownProcesses'),
  },
  { path: ':workspaceShortName', Component: WorkspaceLayer, children: workspaceRoutes },
] as const satisfies readonly OrganizationRouteObject[];

const userRoutes = [
  {
    title: 'Welcome',
    category: 'onboarding',
    path: '/welcome',
    lazy: () => import('~/routes/user/onboarding/views/Welcome'),
  },
  {
    title: 'Settings',
    category: 'user_settings',
    path: '/settings',
    lazy: () => import('~/routes/user/settings/views/Settings'),
    children: [
      { index: true, Component: Outlet, tab: true },
      { path: 'access-credentials', Component: Outlet, tab: true },
      {
        path: 'invitations',
        Component: Outlet,
        restrictions: { user: ['canManageOrganizations'] },
        tab: true,
      },
    ],
  },
  {
    title: 'SSO Management',
    category: 'user_settings',
    restrictions: { userRole: 'ssoRealmAdmin' },
    path: '/settings/sso-management',
    lazy: () => import('~/routes/user/sso-management/views/SsoManagement'),
  },
  {
    path: '/:organizationShortName',
    Component: OrganizationLayer,
    children: organizationRoutes,
  },
] as const satisfies readonly UserRouteObject[];

const homeRoutes = [
  {
    title: 'Not Found',
    category: 'dead_end',
    path: '/invitation-not-found',
    lazy: () => import('~/routes/user/404/InvitationNotFound'),
  },
  {
    title: 'Not Found',
    category: 'dead_end',
    path: '/organization-not-found',
    lazy: () => import('~/routes/organization/404/OrganizationNotFound'),
  },
  {
    title: 'Not Found',
    category: 'dead_end',
    path: '/workspace-not-found',
    lazy: () => import('~/routes/workspace/404/WorkspaceNotFound'),
  },
  {
    path: '/',
    Component: UserRoot,
    children: [{ path: 'home' }],
  },
  {
    title: 'Integration',
    category: 'integrations',
    path: '/link/:applicationId',
    lazy: () => import('~/routes/link/index'),
  },
  {
    title: 'Join Organization',
    category: 'onboarding',
    restrictions: { userRole: 'base' },
    path: '/join-organization',
    lazy: () => import('~/routes/user/onboarding/views/JoinOrganization'),
    children: [{ path: ':token' }],
  },
  {
    title: 'Create Organization',
    category: 'onboarding',
    path: '/create-organization',
    lazy: () => import('~/routes/user/onboarding/views/CreateOrganization'),
  },
  {
    title: 'Join Workspace',
    category: 'onboarding',
    restrictions: { userRole: 'base' },
    path: '/join-workspace/:token',
    lazy: () => import('~/routes/user/join-workspace/views/JoinWorkspace'),
  },
  {
    title: 'Configurator',
    category: 'configurator',
    path: '/configurator',
    lazy: () => import('~/routes/configurator/Configurator'),
  },
  {
    title: 'Configurator',
    category: 'configurator',
    path: '/:organizationShortName/:workspaceShortName/configurator',
    lazy: () => import('~/routes/configurator/Import'),
  },
  {
    title: 'Integration',
    category: 'integrations',
    path: '/github-app-setup',
    lazy: () => import('~/routes/GithubOAuthCallback'),
  },
  {
    title: 'Integration',
    category: 'integrations',
    path: '/gitlab-registration',
    lazy: () => import('~/routes/GitlabRegistrationCallback'),
  },
  {
    category: 'dead_end',
    restrictions: { userRole: 'sso', action: 'notFound' },
    path: '/lobby',
    lazy: () => import('~/routes/user/onboarding/views/Lobby'),
  },
  {
    path: '/organizations/:organizationId/workspaces/:workspaceId/*', // explicit id redirect
    lazy: () => import('~/routes/ExplicitIdRedirect'),
  },
  { Component: UserLayer, children: userRoutes },
] as const satisfies readonly UserRouteObject[];

const appRoutes = [
  {
    Component: RootLayer,
    ErrorBoundary: NotFoundPlaceholder,
    children: [
      { path: '/signin-callback', Component: SigninCallback },
      { path: '/signout-callback', Component: SignoutCallback },
      { path: '/email-not-verified', Component: EmailNotVerified },
      { path: '/user-not-active', Component: AccountNotActive },
      { path: '/signin', Component: SigninPage },
      { Component: ProtectedLayer, children: [{ Component: HomeLayer, children: homeRoutes }] },
    ],
  },
] as const satisfies readonly RouteObject[];

/**
 * All routes defined in appRoutes will be wrapped:
 * - guards based on permissions
 */
const router = createBrowserRouter(appRoutes);
export type AppTabRoutes = ParseTabRoutePaths<typeof appRoutes>;
export type AppRoutes = Exclude<
  ParseRoutePaths<typeof appRoutes>,
  '/:organizationShortName/:workspaceShortName/*'
>;

export const App: FC = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <RouterProvider router={router} fallbackElement={<ViewLoader key="initial" initial />} />
    </ThemeProvider>
    <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
  </QueryClientProvider>
);
